import React, { memo, useMemo, useState } from 'react';

import {
    useGetDailyMetricsSummaryQuery,
    useGetMonthlyMetricHistoryQuery,
} from '../../../pages/metricdashboard/metricDashboardSlice';
import { useGetTeamLeadMonthlyMetricsSummaryQuery } from '../../../pages/scorecardDashboard/scorecardDashboardSlice';
import AgentScoreCardDashboard from './AgentScoreCardDashboard';
import AgentScoreCardDashboardNonPayout from './AgentScoreCardDashboardNonPayout';
import { MetricCardsSkeletonLoader } from '../../../components/metricCardsSkeletonLoader/MetricCardsSkeletonLoader';
import standardMetricsIcon from '../../../assets/standardMetrics.png';
import additionalMetricsIcon from '../../../assets/additionalMetrics.png';
import '../../coachingAction/prepareStep/prepareStep.scss';

interface PropTypes {
    empNum?: number;
    inputyear?: number;
    inputmonth?: number;
    scorecardVersion?: number;
}

const SCORECARD_ROLE_ID = 2;

const AgentScoreCardDashboardTabs = memo(({
    empNum,
    inputyear,
    inputmonth,
    scorecardVersion,
}: PropTypes) => {
    const [activeTab, setActiveTab] = useState<'standard' | 'additional'>('standard');

    const skipQuery = !empNum || !inputyear || !inputmonth; //|| !scorecardVersion;

    const { data: monthlyMetricHistory, isFetching: isHistoryFetching } = useGetMonthlyMetricHistoryQuery(
        {
            employeenumber: empNum,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            scorecardRoleId: SCORECARD_ROLE_ID,
        },
        { skip: skipQuery }
    );

    const { data: dailyMetricsData, isFetching: isDailyFetching } = useGetDailyMetricsSummaryQuery(
        {
            employeenumber: empNum,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            scorecardRoleId: SCORECARD_ROLE_ID,
        },
        { skip: skipQuery }
    );

    const { data: monthlyMetricsData, isFetching: isMonthlyFetching } = useGetTeamLeadMonthlyMetricsSummaryQuery(
        {
            employeenumber: empNum,
            year: inputyear,
            month: inputmonth,
            scorecardVersionId: scorecardVersion,
            scorecardRoleId: SCORECARD_ROLE_ID,
        },
        { skip: !empNum }
    );

    // Rich loading state - previously this whole area just rendered nothing/stale content while
    // any of the 3 queries above were in flight, which reads as a frozen/broken screen on a slow
    // network. Any one of them still fetching is enough to keep showing the skeleton.
    const isMetricsLoading = isHistoryFetching || isDailyFetching || isMonthlyFetching;

    const payoutMetricsData = useMemo(() => {
        const filtered = monthlyMetricsData?.data?.filter((m: any) => m.is_payout_metric === true) ?? [];
        return { data: filtered };
    }, [monthlyMetricsData]);

    const nonPayoutMetricsData = useMemo(() => {
        const filtered = monthlyMetricsData?.data?.filter((m: any) => m.is_payout_metric === false) ?? [];
        return { data: filtered };
    }, [monthlyMetricsData]);

    const hasAdditionalMetrics = nonPayoutMetricsData.data.length > 0;

    return (
        <div>
            {hasAdditionalMetrics && (
            <div className="metrics-tab-switcher">
                <button
                    className={`metrics-tab ${activeTab === 'standard' ? 'metrics-tab--active' : ''}`}
                    onClick={() => setActiveTab('standard')}
                    type="button"
                >
                    <img src={standardMetricsIcon} alt="Standard Metrics" style={{ width: '16px', height: '16px' }} />
                    Standard Metrics
                </button>
                <button
                    className={`metrics-tab ${activeTab === 'additional' ? 'metrics-tab--active' : ''} ${!hasAdditionalMetrics ? 'metrics-tab--disabled' : ''}`}
                    onClick={() => setActiveTab('additional')}
                    disabled={!hasAdditionalMetrics}
                    type="button"
                >
                    <img src={additionalMetricsIcon} alt="Additional Metrics" style={{ width: '16px', height: '16px' }} />
                    Additional Metrics (Non Payout)
                </button>
            </div>
            )}

            <div className="metrics-tab-content">
                {isMetricsLoading ? (
                    <div className="p-3">
                        <MetricCardsSkeletonLoader />
                    </div>
                ) : (
                    <>
                        {activeTab === 'standard' && (
                            <AgentScoreCardDashboard
                                empNum={empNum}
                                inputmonth={inputmonth}
                                inputyear={inputyear}
                                scorecardVersion={scorecardVersion}
                                monthlyMetricsData={payoutMetricsData}
                                monthlyMetricHistory={monthlyMetricHistory}
                                dailyMetricsData={dailyMetricsData}
                            />
                        )}
                        {activeTab === 'additional' && (
                            <AgentScoreCardDashboardNonPayout
                                empNum={empNum}
                                scorecardVersion={scorecardVersion}
                                monthlyMetricsData={nonPayoutMetricsData}
                                monthlyMetricHistory={monthlyMetricHistory}
                                dailyMetricsData={dailyMetricsData}
                            />
                        )}
                    </>
                )}
            </div>
        </div>
    );
});

AgentScoreCardDashboardTabs.displayName = 'AgentScoreCardDashboardTabs';

export default AgentScoreCardDashboardTabs;